import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { v4 as uuidv4 } from 'uuid';

import { TRefItemType, TRefVersionMode } from '../../../types';
import { OutRefBookmarkKind } from '../caption/constants';
import { REFERENCE_NODE_NAME } from './constants';
import Component from './Component';
import { markInserted } from '../../../hooks/useNodeRegistration.hook';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    reference: {
      insertReference: (
        itemId: string,
        itemType: TRefItemType,
        versionMode: TRefVersionMode,
        versionId?: string,
        headingId?: string,
        bookmark?: { id: string; kind: OutRefBookmarkKind }
      ) => ReturnType;
    };
  }
}

const ReferenceNode = Node.create({
  name: REFERENCE_NODE_NAME,

  inline: true,
  group: 'inline',
  atom: true,

  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      itemId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-item'),
        renderHTML: (attributes) => ({ 'data-item': attributes.itemId }),
      },
      itemType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-item-type'),
        renderHTML: (attributes) => ({ 'data-item-type': attributes.itemType }),
      },
      loadedId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-loaded-id'),
        renderHTML: (attributes) => ({ 'data-loaded-id': attributes.loadedId }),
      },
      versionMode: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-version-mode'),
        renderHTML: (attributes) => ({ 'data-version-mode': attributes.versionMode }),
      },
      versionId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-version-id'),
        renderHTML: (attributes) => ({ 'data-version-id': attributes.versionId }),
      },
      headingId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-heading-id'),
        renderHTML: (attributes) => ({ 'data-heading-id': attributes.headingId }),
      },
      bookmark: {
        default: null,
        parseHTML: (element) => {
          const id = element.getAttribute('data-bookmark-id');
          const kind = element.getAttribute('data-bookmark-kind');
          return id ? { id, kind } : null;
        },
        renderHTML: (attributes) =>
          attributes.bookmark
            ? {
                'data-bookmark-id': attributes.bookmark.id,
                'data-bookmark-kind': attributes.bookmark.kind,
              }
            : {},
      },
      originalText: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-original-text'),
        renderHTML: (attributes) =>
          (attributes.bookmark || attributes.headingId) && attributes.originalText
            ? { 'data-original-text': attributes.originalText }
            : {},
      },
      // future: frozen-doc-name, frozen-heading-text, frozen-heading-numbering
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['ref', HTMLAttributes];
  },

  parseHTML() {
    return [
      {
        tag: 'ref',
      },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component, { trackNodeViewPosition: true });
  },

  addCommands() {
    return {
      // ...this.parent?.(),
      insertReference:
        (itemId, itemType, versionMode, versionId, headingId, bookmark) =>
        ({ commands }) => {
          const id = uuidv4();
          markInserted(id);
          return commands.insertContent({
            type: this.name,
            attrs: { id, itemId, itemType, versionMode, versionId, headingId, bookmark },
          });
        },
    };
  },

  // paste-rule is done by Block.tsx
});

export default ReferenceNode;

/* Code for preventing pasting of references:

addProseMirrorPlugins() {
  return [
    new Plugin({
      key: new PluginKey('reference-duplicate-plugin'),
      appendTransaction: (transactions, oldState, newState) => {
        if (!transactions.some(tr => tr.getMeta('paste'))) {
          return;
        }

        let tr = newState.tr;
        let modified = false;

        transactions.forEach(transaction => {
          transaction.steps.forEach(step => {
            const stepMap = step.getMap();
            stepMap.forEach((oldStart, oldEnd, newStart, newEnd) => {
              newState.doc.nodesBetween(newStart, newEnd, (node, pos) => {
                if (node.type.name === 'reference') {
                  let yes = false;
                  try {
                    yes = !oldState.doc.nodeAt(stepMap.map(pos))
                  } catch (e) { yes = true }
                  if (yes) {
                    const newAttrs = {
                      ...node.attrs,
                      id: uuidv4(),
                    };
                    tr = tr.setNodeMarkup(pos, undefined, newAttrs);
                    modified = true;
                  }
                }
              });
            });
          });
        });

        return modified ? tr : null;
      },
    }),
  ];
},
*/
